/**
 * « Ce qu'il reste à faire », en tête de page et rien d'autre autour.
 *
 * ## Pourquoi c'est en haut, et pourquoi c'est la seule chose en haut
 *
 * L'ancien tableau de bord posait trois cartes de même poids — profil, enfants,
 * situation financière — puis deux sections. Rien ne disait par où commencer, et
 * ce qui bloquait réellement l'inscription n'apparaissait nulle part : le parent
 * l'apprenait au panier, après avoir choisi ses stages.
 *
 * Ce bloc répond d'abord à la question que le parent se pose en arrivant, et il
 * disparaît quand la réponse est « rien ». Une bannière permanente qu'on ne peut
 * pas faire taire devient un décor : celle-ci se mérite.
 *
 * ## Pourquoi chaque ligne porte un nombre
 *
 * « Vérifiez vos informations » ne dit pas si c'est l'affaire de dix secondes
 * ou d'un quart d'heure. « 3 informations à compléter · 2 min » borne l'effort,
 * et un effort borné se fait tout de suite.
 */

import { Link } from 'react-router-dom';
import { ChevronRight, ListChecks } from 'lucide-react';
import { resumeEnfant, type CompletudeProfil, type EnfantEvalue } from '../../lib/completude';

/**
 * Le temps que ça prend : une minute, quel que soit le nombre de champs.
 *
 * Un premier jet montait jusqu'à « environ 5 min » au-delà de six manques.
 * C'était faux, et faux dans le mauvais sens : la plupart des familles ont déjà
 * l'information sous la main — le nom du médecin, un niveau de natation — et
 * les cinq questions du droit à l'image sont des boutons oui/non. Annoncer
 * cinq minutes fait remettre à plus tard une tâche qui en prend une.
 */
const DUREE = 'environ 1 minute';

const Ligne = ({
  vers,
  titre,
  detail,
}: {
  vers: string;
  titre: string;
  detail: string;
}) => (
  <Link
    to={vers}
    /*
      `min-h-[56px]` : une cible tactile confortable. Les lignes de 32 px de
      l'ancien tableau se manquaient au pouce, et un clic manqué sur une liste
      qui défile ouvre l'élément voisin.
    */
    className="flex min-h-[56px] items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-amber-200 transition-colors hover:bg-amber-50"
  >
    <span className="min-w-0">
      <span className="block truncate font-medium text-gray-900">{titre}</span>
      {/*
        Le detail n'est pas tronque : c'est lui qui porte le nombre et la
        duree, c'est-a-dire tout l'interet de la ligne. Sur un ecran de 390 px,
        « 10 informations a completer · environ 5 min » devenait
        « 10 informations a completer · environ 5 … ».
      */}
      <span className="block text-sm text-gray-600">{detail}</span>
    </span>
    <ChevronRight className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
  </Link>
);

const BlocAFaire = ({
  profil,
  enfants,
}: {
  profil: CompletudeProfil;
  enfants: EnfantEvalue[];
}) => {
  if (profil.complet && enfants.length === 0) return null;

  const total = enfants.length + (profil.aFaire ? 1 : 0);

  return (
    <section
      aria-labelledby="a-faire"
      className="rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200 sm:p-5"
    >
      <h2 id="a-faire" className="flex items-center gap-2 text-lg font-semibold text-amber-900">
        <ListChecks className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {total === 1 ? 'Une chose à faire' : `${total} choses à faire`}
      </h2>
      <p className="mt-1 text-sm text-amber-900/80">
        Avant de pouvoir inscrire vos enfants à un stage.
      </p>

      <div className="mt-4 space-y-2">
        {profil.aFaire && (
          <Ligne
            vers="/profile"
            titre="Vos coordonnées"
            detail={`${profil.bloquants.length} ${
              profil.bloquants.length === 1 ? 'information à compléter' : 'informations à compléter'
            } · ${DUREE}`}
          />
        )}

        {enfants.map((e) => (
          <Ligne
            key={e.id}
            vers={`/kids/${e.id}/verifier`}
            titre={e.prenom || 'Votre enfant'}
            detail={
              e.completude.complet
                ? `À vérifier — ses informations datent de plus de six semaines · ${DUREE}`
                : `${resumeEnfant(e.completude)} · ${DUREE}`
            }
          />
        ))}
      </div>
    </section>
  );
};

export default BlocAFaire;
